import { Fragment, useCallback, useEffect, useState } from 'react';
import Sidebar from '../components/layout/Sidebar';
import { usePermissions } from '../hooks/usePermissions';
import { PatientCardLink } from '../components/PatientCardLink';
import Header from '../components/layout/Header';
import { execApi } from '../services/api';

// בונוס סטים למנהלי טיפול (שירה 12.8, הכרעות אוהד 30.8). סט = רצף מעקבי מנהל-
// טיפול אחרי פגישה פסיכיאטרית: 4 במכבי ובמכבי נוער, 3 בכללית. התעריף לפי קופה
// ומגיע מהשרת (bonus_by_fund) — אין כאן מספר קשיח שיכול להתיישן.
// המסך משרת שני קהלים: שירה ואבי למעקב, ובאסל לדיווח לשכר.

interface CmRow {
  cm: string; sets: number; amount: number; by_fund: Record<string, number>;
  split: number; stuck: number; stuck_oldest: string | null;
}
interface SetMeeting { day: string; cm: string }
interface Unclosed { serial: string; day: string; fund: string; url: string | null }
interface SetRow {
  serial: string; fund: string; cm: string; follow_ups: number; needed: number;
  anchor_date: string; split: boolean; amount: number;
  meetings: SetMeeting[]; patient_url: string | null;
}
interface FundTotal { sets: number; amount: number }
interface Bonus {
  month: string; bonus_by_fund: Record<string, number>; case_managers: CmRow[];
  total_sets: number; total_amount: number; split_sets: number; stuck_total: number;
  by_fund: Record<string, FundTotal>;
}

const FUND_HE: Record<string, string> = {
  Maccabi: 'מכבי', 'Maccabi Youth': 'מכבי נוער', Clalit: 'כללית',
};

function monthOptions(): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = 0; i < 6; i += 1) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

const ils = (n: number) => `₪${n.toLocaleString()}`;

// Dates arrive as YYYY-MM-DD and are read by Israelis. dd/mm/yy everywhere on this
// screen, including inside the follow-up chains (Ohad 2026-08-31).
const il = (d: string) => (d && d.length >= 10 ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(2, 4)}` : d || '—');

// Pastel chip + dark text — the light language the rest of the app already speaks
// (design decision 2026-08-15). The two funds are told apart by hue rather than by
// reading the word, because "מכבי"/"כללית" at 13px scan identically.
const FUND_STYLE: Record<string, { bg: string; fg: string; bd: string }> = {
  Maccabi: { bg: '#eff6ff', fg: '#1d4ed8', bd: '#bfdbfe' },
  'Maccabi Youth': { bg: '#eef2ff', fg: '#4338ca', bd: '#c7d2fe' },
  Clalit: { bg: '#ecfdf5', fg: '#047857', bd: '#a7f3d0' },
};
const fundChip = (fund: string) => {
  const st = FUND_STYLE[fund] ?? { bg: '#f3f4f6', fg: '#374151', bd: '#e5e7eb' };
  return (
    <span style={{ background: st.bg, color: st.fg, border: `1px solid ${st.bd}`,
      borderRadius: 999, padding: '1px 9px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {FUND_HE[fund] ?? fund}
    </span>
  );
};

export default function ExecCmBonusPage() {
  const canOpenPatient = usePermissions().can('patient_card');
  const months = monthOptions();
  const [month, setMonth] = useState(months[0]);
  const [data, setData] = useState<Bonus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Level two: the sets behind one case manager's number. Fetched on demand — the
  // month payload would carry every meeting of every set otherwise.
  const [cmFilter, setCmFilter] = useState('');
  // Fund filter (Ohad 2026-08-31). It re-prices the table rather than only hiding rows:
  // showing a case manager's FULL bonus next to their Clalit-only set count would be two
  // numbers that do not belong to each other.
  const [fundFilter, setFundFilter] = useState('');
  const [openCm, setOpenCm] = useState<string | null>(null);
  const [sets, setSets] = useState<SetRow[] | null>(null);
  const [unclosed, setUnclosed] = useState<Unclosed[]>([]);
  const [setsLoading, setSetsLoading] = useState(false);

  const toggleCm = (cm: string) => {
    if (openCm === cm) { setOpenCm(null); setSets(null); setUnclosed([]); return; }
    setOpenCm(cm);
    setSets(null);
    setUnclosed([]);
    setSetsLoading(true);
    execApi.getCmBonusSets(month, cm)
      .then((res) => { setSets(res.data.sets || []); setUnclosed(res.data.unclosed || []); })
      .catch(() => setSets([]))
      .finally(() => setSetsLoading(false));
  };

  const load = useCallback((m: string) => {
    setLoading(true);
    setError('');
    execApi.getCmBonus(m)
      .then((res) => setData(res.data))
      .catch(() => setError('הטעינה נכשלה — נסו לרענן'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(month); setOpenCm(null); setSets(null); }, [load, month]);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden" dir="rtl">
        <Header title="בונוס סטים — מנהלי טיפול" />
        <main className="flex-1 overflow-y-auto p-6">
          {/* One surface for the whole screen instead of four cards floating on grey
              (Ohad 2026-08-31). The tiles inside step DOWN to a tinted neutral rather
              than staying white — white on white would have erased the very separation
              the frame is meant to give them. */}
          <div style={{ maxWidth: 1100, margin: '0 auto', background: '#fff',
            border: '1px solid #e5e7eb', borderRadius: 16, padding: '20px 22px 24px',
            boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 8px 24px rgba(16,24,40,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}
              >
                {months.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <select
                value={cmFilter}
                onChange={(e) => setCmFilter(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}
              >
                <option value="">כל מנהלי הטיפול</option>
                {(data?.case_managers ?? []).map((c) => (
                  <option key={c.cm} value={c.cm}>{c.cm}</option>
                ))}
              </select>
              <select
                value={fundFilter}
                onChange={(e) => setFundFilter(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}
              >
                <option value="">כל הקופות</option>
                <option value="Maccabi">מכבי (כולל נוער)</option>
                <option value="Clalit">כללית</option>
              </select>
            </div>

            {error && <div style={{ color: '#b91c1c' }}>{error}</div>}
            {loading && <div style={{ color: '#6b7280' }}>טוען…</div>}

            {data && !loading && (
              <>
                {/* Two rows on purpose. The top one is the month's answer; the bottom is
                    where the money came from and what is holding more of it up. A single
                    six-tile row read as six equals, and the two fund tiles are a breakdown
                    of the two above them, not siblings of them. */}
                <div style={{ display: 'flex', gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
                  {[
                    { label: 'סטים שהושלמו', v: data.total_sets.toLocaleString(), c: '#0f172a', bar: '#cbd5e1' },
                    { label: 'סך הבונוס', v: ils(data.total_amount), c: '#047857', bar: '#6ee7b7' },
                  ].map((t) => (
                    <div key={t.label} style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 12,
                      padding: '12px 18px', minWidth: 190, borderTop: `3px solid ${t.bar}` }}>
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{t.label}</div>
                      <div style={{ fontSize: 26, fontWeight: 700, color: t.c }}>{t.v}</div>
                    </div>
                  ))}
                  {/* Sets AND money per fund, priced on the server — the two rates differ,
                      so a fund's set count without its own total says very little. */}
                  {(['Maccabi', 'Clalit'] as const).map((f) => {
                    const ft = data.by_fund?.[f] ?? { sets: 0, amount: 0 };
                    const st = FUND_STYLE[f];
                    return (
                      <div key={f} style={{ background: st.bg, border: `1px solid ${st.bd}`, borderRadius: 12,
                        padding: '12px 18px', minWidth: 190, borderTop: `3px solid ${st.fg}` }}>
                        <div style={{ fontSize: 12, color: st.fg, marginBottom: 4, fontWeight: 600 }}>
                          {FUND_HE[f]} · ₪{data.bonus_by_fund?.[f] ?? '—'} לסט
                        </div>
                        <div style={{ fontSize: 26, fontWeight: 700, color: st.fg }}>
                          {ils(ft.amount)}
                          <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.75 }}>
                            {'  '}· {ft.sets} סטים
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
                  {[
                    { label: 'מפוצלים בין מטפלים', v: data.split_sets.toLocaleString(), c: '#b45309', bg: '#fffbeb', bd: '#fde68a' },
                    { label: 'פגישות בסטטוס אקטיב', v: data.stuck_total.toLocaleString(), c: '#b91c1c', bg: '#fef2f2', bd: '#fecaca' },
                  ].map((t) => (
                    <div key={t.label} style={{ background: t.bg, border: `1px solid ${t.bd}`, borderRadius: 12,
                      padding: '10px 18px', minWidth: 190 }}>
                      <div style={{ fontSize: 12, color: t.c, marginBottom: 2, fontWeight: 600 }}>{t.label}</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: t.c }}>{t.v}</div>
                    </div>
                  ))}
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff',
                  border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', fontSize: 14,
                  tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      {['מנהל טיפול', 'סטים', 'בונוס', 'לפי קופה', 'מפוצל', 'בסטטוס אקטיב'].map((h) => (
                        <th key={h}
                          title={h === 'בסטטוס אקטיב'
                            ? 'פגישות מעקב שהתקיימו אבל נשארו בסטטוס "פעילה" — איש לא סגר אותן. רק פגישה סגורה נספרת לסט.'
                            : undefined}
                          style={{ padding: '9px 12px', background: '#f9fafb', textAlign: 'right',
                            borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.case_managers
                      .filter((c) => !cmFilter || c.cm === cmFilter)
                      .map((c) => {
                        // Under a fund filter the row shows THAT fund's sets and THAT fund's
                        // money, re-priced from the same rates the server used. Without this
                        // the sets column would narrow and the bonus column would not.
                        if (!fundFilter) return { ...c, shownSets: c.sets, shownAmount: c.amount };
                        const funds = Object.entries(c.by_fund).filter(([f]) =>
                          fundFilter === 'Clalit' ? f === 'Clalit' : f !== 'Clalit');
                        const shownSets = funds.reduce((a, [, n]) => a + n, 0);
                        const shownAmount = funds.reduce(
                          (a, [f, n]) => a + n * (data.bonus_by_fund?.[f] ?? 0), 0);
                        return { ...c, shownSets, shownAmount,
                          by_fund: Object.fromEntries(funds) as Record<string, number> };
                      })
                      .filter((c) => !fundFilter || c.shownSets > 0 || c.stuck > 0)
                      .map((c) => (
                      <Fragment key={c.cm}>
                      <tr onClick={() => toggleCm(c.cm)}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = openCm === c.cm ? '#f8fafc' : 'transparent'; }}
                        style={{ cursor: 'pointer', background: openCm === c.cm ? '#f8fafc' : 'transparent',
                          transition: 'background 120ms' }}>
                        <td style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6', fontWeight: 600 }}>
                          <span style={{ color: '#6b7280', marginLeft: 6 }}>{openCm === c.cm ? '▾' : '▸'}</span>
                          {c.cm}
                        </td>
                        <td style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6' }}>{c.shownSets}</td>
                        <td style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6', fontWeight: 700,
                          color: c.shownAmount > 0 ? '#047857' : '#64748B' }}>{ils(c.shownAmount)}</td>
                        <td style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6' }}>
                          <span style={{ display: 'inline-flex', gap: 5, flexWrap: 'wrap' }}>
                            {Object.entries(c.by_fund).length === 0 && <span style={{ color: '#64748B' }}>—</span>}
                            {Object.entries(c.by_fund).map(([f, n]) => (
                              <span key={f} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                {fundChip(f)}
                                <span style={{ color: '#4b5563', fontVariantNumeric: 'tabular-nums' }}>{n}</span>
                              </span>
                            ))}
                          </span>
                        </td>
                        <td style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6',
                          color: c.split > 0 ? '#b45309' : '#64748B' }}
                          title="הרצף עבר בין שני מנהלי טיפול. הסט משולם — למי שסגר אותו.">
                          {c.split || '—'}
                        </td>
                        {/* Stuck follow-ups are the case manager's own to close, and each one is a
                            set that cannot complete — so it is shown as money, not as a count. */}
                        <td style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6',
                          color: c.stuck > 0 ? '#b91c1c' : '#64748B' }}
                          title={c.stuck_oldest ? `הישן ביותר: ${il(c.stuck_oldest)}` : undefined}>
                          {c.stuck ? `${c.stuck}` : '—'}
                        </td>
                      </tr>
                      {openCm === c.cm && (
                        <tr>
                          <td colSpan={6} style={{ padding: '10px 16px', background: '#f9fafb',
                            borderBottom: '1px solid #e5e7eb' }}>
                            {setsLoading && <span style={{ color: '#6b7280', fontSize: 13 }}>טוען סטים…</span>}
                            {sets && !setsLoading
                              && sets.filter((st) => !fundFilter
                                || (fundFilter === 'Clalit' ? st.fund === 'Clalit' : st.fund !== 'Clalit')).length === 0 && (
                              <span style={{ color: '#6b7280', fontSize: 13 }}>
                                {fundFilter ? 'אין סטים בקופה הזו החודש.' : 'אין סטים שנסגרו החודש.'}
                              </span>
                            )}
                            {unclosed.length > 0 && (
                              <div style={{ marginBottom: 12, padding: '8px 10px', background: '#fef2f2',
                                border: '1px solid #fecaca', borderRadius: 8 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c', marginBottom: 6 }}>
                                  פגישות בסטטוס אקטיב ({unclosed.length}) — כל אחת חוסמת סט
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                  {unclosed.map((u) => {
                                    const label = `${il(u.day)} · ${u.serial}`;
                                    return u.url ? (
                                      <PatientCardLink key={`${u.serial}-${u.day}`} linked={canOpenPatient} href={u.url}
                                        title="פתיחת הפגישה בתיק המטופל לסגירה"
                                        style={{ fontSize: 12, color: canOpenPatient ? '#2563eb' : '#6b7280', background: '#fff',
                                          border: '1px solid #e5e7eb', borderRadius: 6, padding: '2px 7px' }}>
                                        {canOpenPatient ? `${label} ↗` : label}
                                      </PatientCardLink>
                                    ) : (
                                      <span key={`${u.serial}-${u.day}`} style={{ fontSize: 12, color: '#6b7280',
                                        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '2px 7px' }}>
                                        {label}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {sets && sets.length > 0 && (
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                  <tr>
                                    {['מטופל', 'קופה', 'פגישת פסיכיאטר אחרונה', 'מעקבים', 'תאריכי המעקבים', 'משולם'].map((h) => (
                                      <th key={h} style={{ padding: '5px 8px', textAlign: 'right',
                                        color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {sets
                                    .filter((st) => !fundFilter
                                      || (fundFilter === 'Clalit' ? st.fund === 'Clalit' : st.fund !== 'Clalit'))
                                    .map((st) => (
                                    <tr key={`${st.serial}-${st.anchor_date}`}>
                                      <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>
                                        {st.patient_url
                                          ? <PatientCardLink linked={canOpenPatient} href={st.patient_url}
                                              title="פתיחת תיק המטופל במערכת — לאימות מול הרשומה הקלינית"
                                              style={{ color: '#2563eb' }}>{canOpenPatient ? `${st.serial} ↗` : st.serial}</PatientCardLink>
                                          : st.serial}
                                      </td>
                                      <td style={{ padding: '5px 8px' }}>{fundChip(st.fund)}</td>
                                      <td style={{ padding: '5px 8px', color: '#6b7280',
                                        fontVariantNumeric: 'tabular-nums' }}>{il(st.anchor_date)}</td>
                                      <td style={{ padding: '5px 8px' }}>{st.follow_ups}/{st.needed}</td>
                                      {/* All the set's meetings are listed — they are what
                                          defines it — and in a split run the ones belonging to
                                          someone else are named, so the payment explains itself.
                                          dir="ltr" so the chain runs OLDEST on the left to
                                          NEWEST on the right (Ohad 2026-08-31): inside an RTL
                                          table the first item lands on the right, which put the
                                          arrows against the direction time actually moves. */}
                                      <td dir="ltr" style={{ padding: '5px 8px', color: '#374151',
                                        textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                        {st.meetings.map((m, mi) => (
                                          <span key={`${m.day}-${mi}`}>
                                            {mi > 0 && <span style={{ color: '#64748B' }}> → </span>}
                                            <span style={{ color: m.cm === st.cm ? undefined : '#b45309' }}>
                                              {il(m.day)}
                                              {m.cm !== st.cm && ` (${m.cm})`}
                                            </span>
                                          </span>
                                        ))}
                                      </td>
                                      <td style={{ padding: '5px 8px', fontWeight: 700,
                                        color: '#047857' }}>
                                        ₪{st.amount}
                                        {st.split && (
                                          <span style={{ color: '#b45309', fontWeight: 400 }}> · מפוצל</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>

                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 12, lineHeight: 1.7 }}>
                  <b>מפוצל</b> — רצף שעבר בין שני מנהלי טיפול. הסט משולם, ומקבל אותו מי שביצע את
                  המעקב שסגר אותו. התאריכים של האחרים מסומנים בכתום עם שמם.<br />
                  <b>פגישות בסטטוס אקטיב</b> — מעקבים שהתקיימו אבל עדיין בסטטוס "פעילה", כלומר איש לא סגר
                  אותם. רק פגישה סגורה נספרת לסט, ולכן כל פגישה פתוחה היא סט שלא ייסגר. סגירת
                  הפגישה מחזירה אותה למניין.
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
